import type { ComponentType, SVGProps } from "react";

import {
  AccountsIcon,
  DashboardIcon,
  EventsIcon,
  PostsIcon,
  ReportsIcon,
  SettingsIcon,
} from "@/src/components/icons";
import type { Translations } from "@/src/i18n/translations";
import type { Route } from "@/src/routes/paths";
import { ROUTES } from "@/src/routes/paths";

type NavigationKey = keyof Translations["navigation"];

export type NavItem = {
  /** Clé du scope `navigation` — sert de libellé **et** de titre de page. */
  key: NavigationKey;
  path: Route;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

export type NavGroup = {
  /** Intitulé de section, en petites capitales dans la barre latérale. */
  key: NavigationKey;
  items: NavItem[];
};

/**
 * **Source unique de la navigation.**
 *
 * Un seul tableau alimente trois choses qui, séparées, divergeraient :
 * les liens de la barre latérale, ceux du tiroir mobile, et le **titre affiché
 * par l'en-tête**. Ajouter un écran, c'est ajouter une entrée ici et une route
 * dans `appRoutes` — rien d'autre.
 *
 * ⚠️ Les libellés ne sont pas ici : seulement des clés du dictionnaire. Une
 * clé inconnue est une erreur de `tsc`.
 */
export const NAVIGATION: NavGroup[] = [
  {
    key: "group-overview",
    items: [{ key: "dashboard", path: ROUTES.dashboard, Icon: DashboardIcon }],
  },
  {
    key: "group-moderation",
    items: [
      { key: "reports", path: ROUTES.reports, Icon: ReportsIcon },
      /*
        La file du fil est une **seconde** entrée de modération, et non un
        onglet des signalements : les deux ne se remplissent pas de la même
        façon. Un signalement vient d'un utilisateur ; une publication en
        attente vient du classifieur, qui ne refuse jamais et met en attente ce
        dont il n'est pas sûr. Fondues, la file automatique noierait la file
        humaine — celle qui porte l'échéance de 24 h.
      */
      { key: "posts", path: ROUTES.posts, Icon: PostsIcon },
    ],
  },
  {
    key: "group-community",
    items: [
      { key: "events", path: ROUTES.events, Icon: EventsIcon },
      { key: "accounts", path: ROUTES.accounts, Icon: AccountsIcon },
    ],
  },
  {
    key: "group-system",
    items: [{ key: "settings", path: ROUTES.settings, Icon: SettingsIcon }],
  },
];

/** Tous les écrans, à plat — l'ordre des groupes est conservé. */
export const NAV_ITEMS: NavItem[] = NAVIGATION.flatMap((group) => group.items);

/**
 * L'entrée de navigation correspondant à une URL.
 *
 * ⚠️ **Le tableau de bord se compare en égalité stricte, pas en préfixe.**
 * Son chemin est `/`, dont *toute* URL est préfixée : un `startsWith` naïf le
 * déclarerait actif sur `/events`, et deux liens s'allumeraient à la fois.
 *
 * Les autres se comparent en préfixe pour que les futures sous-routes
 * (`/events/42`) gardent leur parent allumé.
 */
export function findNavItem(pathname: string): NavItem | undefined {
  return NAV_ITEMS.find((item) =>
    item.path === ROUTES.dashboard
      ? pathname === item.path
      : pathname === item.path || pathname.startsWith(`${item.path}/`),
  );
}
